import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import LottieIllustration from './LottieIllustration';
import { loadAnimation } from '../utils/loadAnimation';

interface Project {
  id: number;
  title: string;
  category: string;
  description: string;
  image: string;
  technologies: string[];
  features: string[];
  client: string;
  duration: string;
  link?: string;
}

const Portfolio: React.FC = () => {
  const [activeFilter, setActiveFilter] = useState('All');
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [websiteAnimation, setWebsiteAnimation] = useState<any>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const projectsRef = useRef<HTMLDivElement>(null);

  const projects: Project[] = [
    {
      id: 1,
      title: 'Dollar EH',
      category: 'E-commerce',
      description: 'A comprehensive multi-vendor e-commerce platform with advanced features',
      image: '/assets/clients/dollar-eh.png',
      technologies: ['Shopify', 'React', 'Node.js', 'MySQL', 'Stripe'],
      features: ['Multi-vendor Support', 'Real-time Chat', 'Advanced Analytics', 'Mobile App'],
      client: 'Dollar EH Canada',
      duration: '6 months',
      link: 'https://dollareh.ca/'
    },
    {
      id: 2,
      title: 'EZee Infotech',
      category: 'Mobile',
      description: 'EZee Infotech, a trusted partner in logistics data management System',
      image: '/assets/clients/ezee-info-logo.png',
      technologies: ['Flutter', 'Firebase', 'Google Maps API'],
      features: ['Real-time Tracking', 'Push Notifications', 'Payment Integration'],
      client: 'EZee Infotech',
      duration: '12 months',
      link: 'https://www.ezeeinfocloudsolutions.com/'
    },
    {
      id: 3,
      title: 'Sridevi Group Of Companies',
      category: 'Web',
      description: 'A full-stack support—from ecommerce to inventory and accounting systems.',
      image: '/assets/clients/sri-devi-group.png',
      technologies: ['Next.js', 'Prisma', 'PostgreSQL', 'AWS'],
      features: ['Inventory Management', 'Payment Integration', 'Admin Portal', 'CRM Integration'],
      client: 'Sridevi Group Of Companies',
      duration: '8 months',
      link: 'https://sridevigroup.co.in/'
    },
    {
      id: 4,
      title: 'VET Group of Institutions',
      category: 'Web',
      description: 'VETIAS partnered with us for second-year students training included real-time projects and software engineering best practices.',
      image: '/assets/clients/vetias.png',
      technologies: ['Java', 'Spring Boot', 'MySQL'],
      features: ['Real-time Projects', 'GitHub Usage', 'TDD', 'Software Engineering Best Practices'],
      client: 'VET Group of Institutions',
      duration: '5 months',
      link: 'https://vetias.ac.in/'
    },
    {
      id: 5,
      title: 'Aurzart',
      category: 'E-commerce',
      description: 'An application into the world of no-code technology, empowering customer to manage their online business independently',
      image: '/assets/clients/aurzart-red-logo.png',
      technologies: ['Flutter', 'Firebase', 'WebRTC', 'HIPAA Compliance'],
      features: ['Payment Integration', 'Inventory Management', 'Admin Portal', 'CRM Integration'],
      client: 'Aurzart',
      duration: '12 months',
      link: 'https://aurzart.com/'
    }
  ];

  const filters = ['All', 'E-commerce', 'Mobile', 'Web'];

  const filteredProjects = activeFilter === 'All' 
    ? projects 
    : projects.filter(project => project.category === activeFilter);

  useEffect(() => {
    const loadWebsiteAnimation = async () => {
      try {
        const animation = await loadAnimation('/animations/website.json');
        setWebsiteAnimation(animation);
      } catch (error) {
        console.error('Error loading website animation:', error);
      }
    };

    loadWebsiteAnimation();
  }, []);

  const openModal = (project: Project) => {
    setSelectedProject(project);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setSelectedProject(null);
    document.body.style.overflow = 'unset';
  };

  // Handle Escape key to close modal
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && selectedProject) {
        closeModal();
      }
    };

    if (selectedProject) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedProject]);

  return (
    <>
      <section ref={sectionRef} id="portfolio" className="py-10 dark:bg-navy-light light:bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold dark:text-white light:text-gray-900 mb-6">
              Featured <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-accent-light">Projects</span>
            </h2>
            <p className="text-xl dark:text-gray-300 text-gray-600 max-w-3xl mx-auto">
              Explore our portfolio of successful projects that have transformed businesses 
              and delivered exceptional results.
            </p>
          </div>

          {/* Animation and Portfolio Description */}
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            {/* Left Side - Animation */}
            <div className="order-1 lg:order-1">
              <div className="bg-gradient-to-br from-accent/5 to-accent-light/5 rounded-3xl p-8">
                {websiteAnimation ? (
                  <LottieIllustration
                    animationData={websiteAnimation}
                    className="w-full h-full"
                    width="100%"
                    height="400px"
                    loop={true}
                    autoplay={true}
                  />
                ) : (
                  <div className="flex items-center justify-center h-[400px]">
                    <div className="text-center">
                      <div className="text-6xl mb-4">🌐</div>
                      <div className="text-lg font-semibold dark:text-white text-gray-900 mb-2">Web Development</div>
                      <div className="text-sm dark:text-gray-400 text-gray-500">Modern web applications</div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right Side - Portfolio Description */}
            <div className="order-2 lg:order-2">
              <h3 className="text-3xl font-bold dark:text-white light:text-gray-900 mb-6">
                Innovative <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-accent-light">Solutions</span>
              </h3>
              <p className="text-lg dark:text-gray-300 light:text-gray-600 mb-8 leading-relaxed">
                Our portfolio showcases diverse projects across industries, from e-commerce platforms 
                to mobile applications, each designed to solve real-world challenges.
              </p>
              <div className="space-y-4">
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-accent rounded-full mr-3"></div>
                  <span className="dark:text-gray-300 light:text-gray-600">E-commerce & marketplace solutions</span>
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-accent rounded-full mr-3"></div>
                  <span className="dark:text-gray-300 light:text-gray-600">Mobile app development</span>
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-accent rounded-full mr-3"></div>
                  <span className="dark:text-gray-300 light:text-gray-600">Web applications & platforms</span>
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-accent rounded-full mr-3"></div>
                  <span className="dark:text-gray-300 light:text-gray-600">AI-powered solutions</span>
                </div>
              </div>
            </div>
          </div>

          {/* Filter Buttons */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {filters.map((filter) => (
              <button
                key={filter}
                onClick={() => setActiveFilter(filter)}
                className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
                  activeFilter === filter
                    ? 'bg-accent text-white shadow-lg shadow-accent/25'
                    : 'glass dark:text-gray-300 text-gray-600 hover:dark:text-white hover:text-gray-900 hover:bg-opacity-30'
                }`}
              >
                {filter}
              </button>
            ))}
          </div>

          {/* Projects Grid */}
          <div ref={projectsRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ y: 60, opacity: 0, scale: 0.95 }}
                whileInView={{ y: 0, opacity: 1, scale: 1 }}
                viewport={{ once: false, margin: "-50px" }}
                transition={{ 
                  duration: 0.5,
                  delay: index * 0.1,
                  ease: [0.25, 0.46, 0.45, 0.94],
                  type: "tween"
                }}
                className="group"
              >
                <div 
                  className="glass rounded-2xl overflow-hidden cursor-pointer transform hover:scale-105 hover:-translate-y-2 transition-all duration-500"
                  onClick={() => openModal(project)}
                >
                  {/* Project Image */}
                  <div className="relative h-48 w-full bg-white from-accent/20 to-accent-light/20 overflow-hidden flex items-center justify-center">
                    <img 
                      src={project.image} 
                      alt={`${project.title} project screenshot`} 
                      className="max-w-full max-h-full object-contain object-center" 
                      style={{ 
                        objectFit: 'contain', 
                        objectPosition: 'center',
                        width: 'auto',
                        height: 'auto'
                      }}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const parent = target.parentElement;
                        if (parent) {
                          parent.innerHTML = `
                            <div class="w-full h-full flex items-center justify-center bg-gradient-to-br from-accent/30 to-accent-light/30">
                              <div class="text-6xl opacity-50">📱</div>
                            </div>
                          `;
                        }
                      }}
                    /> 

                    <div className="absolute top-4 right-4 px-3 py-1 glass rounded-full text-xs font-semibold text-black">
                      {project.category}
                    </div>
                  </div>

                  {/* Project Info */}
                  <div className="p-6">
                    <h3 className="text-xl font-semibold dark:text-white text-gray-900 mb-2 group-hover:text-accent-light transition-colors duration-300">
                      {project.title}
                    </h3>
                    <p className="dark:text-gray-300 text-gray-700 text-sm mb-4 line-clamp-2">
                      {project.description}
                    </p>
                    
                    {/* Technologies */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {project.technologies.slice(0, 3).map((tech, techIndex) => (
                        <span key={techIndex} className="px-2 py-1 bg-accent/20 text-accent-light text-xs rounded-full">
                          {tech}
                        </span>
                      ))}
                      {project.technologies.length > 3 && (
                        <span className="px-2 py-1 dark:bg-gray-600/20 bg-gray-300/20 dark:text-gray-400 text-gray-500 text-xs rounded-full">
                          +{project.technologies.length - 3}
                        </span>
                      )}
                    </div>

                    {/* View Project Button */}
                    <div className="flex items-center text-accent-light text-sm font-semibold group-hover:text-accent transition-colors duration-300">
                      View Project
                      <svg className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Project Modal */}
      {selectedProject && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-75 backdrop-blur-sm"
          onClick={closeModal}
        >
          <div 
            className="glass rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              {/* Modal Header */}
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h3 className="text-3xl font-bold dark:text-white text-gray-900 mb-2">{selectedProject.title}</h3>
                  <p className="dark:text-gray-300 text-gray-600">{selectedProject.client} • {selectedProject.duration}</p>
                </div>
                <button
                  onClick={closeModal}
                  className="p-2 hover:bg-white hover:bg-opacity-10 rounded-full transition-colors duration-300"
                >
                  <svg className="w-6 h-6 dark:text-gray-400 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Project Image */}
              <div className="relative h-64 bg-white rounded-xl mb-6 overflow-hidden">
                <div className="absolute inset-0 bg-white flex items-center justify-center">
                  <div className="text-8xl opacity-100">
                  <img 
                      src={selectedProject.image} 
                      alt={`${selectedProject.title} project screenshot`} 
                      className="max-w-full max-h-full object-contain object-center" 
                      style={{ 
                        objectFit: 'contain', 
                        objectPosition: 'center',
                        width: 'auto',
                        height: 'auto'
                      }}
                    /> 

                  </div>
                </div>
              </div>

              {/* Project Details */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-xl font-semibold dark:text-white text-gray-900 mb-4">Description</h4>
                  <p className="dark:text-gray-300 text-gray-600 mb-6">{selectedProject.description}</p>

                  <h4 className="text-xl font-semibold dark:text-white text-gray-900 mb-4">Key Features</h4>
                  <ul className="space-y-2">
                    {selectedProject.features.map((feature, index) => (
                      <li key={index} className="flex items-center dark:text-gray-300 text-gray-600">
                        <span className="w-2 h-2 bg-accent rounded-full mr-3"></span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h4 className="text-xl font-semibold dark:text-white text-gray-900 mb-4">Technologies Used</h4>
                  <div className="flex flex-wrap gap-2 mb-6">
                    {selectedProject.technologies.map((tech, index) => (
                      <span key={index} className="px-3 py-1 bg-accent/20 text-accent-light rounded-full">
                        {tech}
                      </span>
                    ))}
                  </div>

                  {selectedProject.link && (
                    <a
                      href={selectedProject.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-6 py-3 bg-accent hover:bg-accent-dark text-white font-semibold rounded-full transition-all duration-300 transform hover:scale-105"
                    >
                      View Live Project
                      <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Portfolio;
